/**
 * Opportunity Detail Page - Sales Flywheel State Machine
 * 
 * Linear, focused UX per the spec:
 * "Every screen answers: What is the ONE next action?"
 * 
 * This page shows:
 * 1. Current state prominently
 * 2. The ONE primary action button
 * 3. Timeline of all actions taken
 * 4. Source attribution and contact info
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Skeleton,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  stepConnectorClasses,
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
  useTheme,
  alpha,
  styled,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  PlayArrow as PursueIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Description as ProposalIcon,
  EmojiEvents as WinIcon,
  Cancel as LoseIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  History as HistoryIcon,
  Source as SourceIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as PendingIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import stratagemService, {
  SalesOpportunity,
  OpportunityAction,
  OpportunityState,
} from '../services/stratagemService';

// State order for stepper
const stateOrder: OpportunityState[] = [
  'detected',
  'pursued',
  'outreach_sent',
  'engaged',
  'proposal_sent',
  'contract_won',
];

const stateLabels: Record<OpportunityState, string> = {
  detected: 'Detected',
  pursued: 'Pursued',
  outreach_sent: 'Outreach Sent',
  engaged: 'Engaged',
  proposal_sent: 'Proposal Sent',
  contract_won: 'Won',
  contract_lost: 'Lost',
};

const stateColors: Record<OpportunityState, string> = {
  detected: '#9e9e9e',
  pursued: '#2196f3',
  outreach_sent: '#ff9800',
  engaged: '#4caf50',
  proposal_sent: '#9c27b0',
  contract_won: '#00c853',
  contract_lost: '#f44336',
};

// Custom step connector
const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: 'linear-gradient(95deg, #00c853 0%, #4caf50 100%)',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: 'linear-gradient(95deg, #00c853 0%, #4caf50 100%)',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0',
    borderRadius: 1,
  },
}));

// Custom step icon
const ColorlibStepIconRoot = styled('div')<{
  ownerState: { completed?: boolean; active?: boolean; lost?: boolean };
}>(({ theme, ownerState }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#ccc',
  zIndex: 1,
  color: '#fff',
  width: 44,
  height: 44,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  ...(ownerState.active && {
    backgroundImage: 'linear-gradient(136deg, #2196f3 0%, #1976d2 100%)',
    boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
  }),
  ...(ownerState.completed && {
    backgroundImage: 'linear-gradient(136deg, #00c853 0%, #4caf50 100%)',
  }),
  ...(ownerState.lost && {
    backgroundImage: 'linear-gradient(136deg, #f44336 0%, #d32f2f 100%)',
  }),
}));

function ColorlibStepIcon(props: { active?: boolean; completed?: boolean; lost?: boolean; icon: React.ReactNode }) {
  const { active, completed, lost, icon } = props;
  return (
    <ColorlibStepIconRoot ownerState={{ completed, active, lost }}>
      {completed ? <CheckIcon /> : icon}
    </ColorlibStepIconRoot>
  );
}

const stateIcons: Record<OpportunityState, React.ReactNode> = {
  detected: <SourceIcon />,
  pursued: <PursueIcon />,
  outreach_sent: <EmailIcon />,
  engaged: <PhoneIcon />,
  proposal_sent: <ProposalIcon />,
  contract_won: <WinIcon />,
  contract_lost: <LoseIcon />,
};

const OpportunityDetailPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  // State
  const [loading, setLoading] = useState(true);
  const [opportunity, setOpportunity] = useState<SalesOpportunity | null>(null);
  const [history, setHistory] = useState<OpportunityAction[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    actionType: string;
    nextState: OpportunityState | null;
  }>({ open: false, actionType: '', nextState: null });
  const [actionLoading, setActionLoading] = useState(false);
  const [formData, setFormData] = useState<Record<string, any>>({});
  
  // Load data
  useEffect(() => {
    if (id) loadData();
  }, [id]);
  
  const loadData = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [opp, historyData] = await Promise.all([
        stratagemService.getOpportunity(id),
        stratagemService.getOpportunityHistory(id),
      ]);
      setOpportunity(opp);
      setHistory(historyData.actions || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load opportunity');
    } finally {
      setLoading(false);
    }
  };
  
  const getNextAction = (): { action: string; nextState: OpportunityState } | null => {
    if (!opportunity) return null;
    const stateActions: Record<OpportunityState, { action: string; nextState: OpportunityState } | null> = {
      detected: { action: 'Pursue This Opportunity', nextState: 'pursued' },
      pursued: { action: 'Launch Outreach', nextState: 'outreach_sent' },
      outreach_sent: { action: 'Log Engagement', nextState: 'engaged' },
      engaged: { action: 'Send Proposal', nextState: 'proposal_sent' },
      proposal_sent: { action: 'Mark as Won', nextState: 'contract_won' },
      contract_won: null,
      contract_lost: null,
    };
    return stateActions[opportunity.state];
  };
  
  const handlePrimaryAction = () => {
    const next = getNextAction();
    if (next) {
      setFormData({});
      setActionDialog({ open: true, actionType: next.action, nextState: next.nextState });
    }
  };
  
  const handleLoseAction = () => {
    setFormData({});
    setActionDialog({ open: true, actionType: 'Mark as Lost', nextState: 'contract_lost' });
  };
  
  const executeAction = async () => {
    if (!opportunity || !actionDialog.nextState) return;
    
    setActionLoading(true);
    try {
      switch (actionDialog.nextState) {
        case 'pursued':
          await stratagemService.pursueOpportunity(opportunity.id, {
            priority: formData.priority || 'medium',
            notes: formData.notes,
          });
          break;
        case 'outreach_sent':
          await stratagemService.launchOutreach(opportunity.id, {
            channel: formData.channel || 'email',
            template_id: formData.template_id,
            custom_message: formData.custom_message,
            contact_email: formData.contact_email || opportunity.contact_email,
            contact_name: formData.contact_name || opportunity.contact_name,
          });
          break;
        case 'engaged':
          await stratagemService.logEngagement(opportunity.id, {
            engagement_type: formData.engagement_type || 'reply',
            engagement_notes: formData.engagement_notes || 'Engagement logged',
            next_action: formData.next_action,
          });
          break;
        case 'proposal_sent':
          await stratagemService.sendProposal(opportunity.id, {
            proposal_title: formData.proposal_title || 'Service Proposal',
            proposal_amount: parseFloat(formData.proposal_amount) || parseFloat(String(opportunity.estimated_value)) || 0,
            proposal_terms: formData.proposal_terms,
          });
          break;
        case 'contract_won':
          await stratagemService.winContract(opportunity.id, {
            contract_value: parseFloat(formData.contract_value) || parseFloat(String(opportunity.estimated_value)) || 0,
            contract_terms: formData.contract_terms,
          });
          break;
        case 'contract_lost':
          await stratagemService.loseContract(opportunity.id, {
            loss_reason: formData.loss_reason || 'Not specified',
            notes: formData.notes,
          });
          break;
      }
      
      setActionDialog({ open: false, actionType: '', nextState: null });
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };
  
  const formatCurrency = (value: number | string | null) => {
    if (!value) return '-';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(num);
  };
  
  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };
  
  const getSourceLabel = () => {
    if (!opportunity) return '';
    if (opportunity.title) return opportunity.title;
    if (opportunity.contact_company) return opportunity.contact_company;
    if (opportunity.detection_rationale?.company_name) return opportunity.detection_rationale.company_name;
    return opportunity.source_engine;
  };
  
  const getCurrentStepIndex = () => {
    if (!opportunity) return 0;
    if (opportunity.state === 'contract_lost') return -1;
    return stateOrder.indexOf(opportunity.state);
  };
  
  // Render action dialog content
  const renderDialogContent = () => {
    switch (actionDialog.nextState) {
      case 'pursued':
        return (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority || 'medium'}
                label="Priority"
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Notes"
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
          </>
        );
      case 'outreach_sent':
        return (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Channel</InputLabel>
              <Select
                value={formData.channel || 'email'}
                label="Channel"
                onChange={(e) => setFormData({ ...formData, channel: e.target.value })}
              >
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="phone">Phone</MenuItem>
                <MenuItem value="sms">SMS</MenuItem>
                <MenuItem value="linkedin">LinkedIn</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              sx={{ mb: 2 }}
              label="Contact Name"
              value={formData.contact_name || opportunity?.contact_name || ''}
              onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
            />
            <TextField
              fullWidth
              sx={{ mb: 2 }}
              label="Contact Email"
              value={formData.contact_email || opportunity?.contact_email || ''}
              onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Custom Message (optional)"
              value={formData.custom_message || ''}
              onChange={(e) => setFormData({ ...formData, custom_message: e.target.value })}
            />
          </>
        );
      case 'engaged':
        return (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Engagement Type</InputLabel>
              <Select
                value={formData.engagement_type || 'reply'}
                label="Engagement Type"
                onChange={(e) => setFormData({ ...formData, engagement_type: e.target.value })}
              >
                <MenuItem value="reply">Email Reply</MenuItem>
                <MenuItem value="call">Phone Call</MenuItem>
                <MenuItem value="meeting">Meeting</MenuItem>
                <MenuItem value="demo">Demo</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Engagement Notes"
              required
              value={formData.engagement_notes || ''}
              onChange={(e) => setFormData({ ...formData, engagement_notes: e.target.value })}
            />
            <TextField
              fullWidth
              sx={{ mt: 2 }}
              label="Next Action"
              value={formData.next_action || ''}
              onChange={(e) => setFormData({ ...formData, next_action: e.target.value })}
            />
          </>
        );
      case 'proposal_sent':
        return (
          <>
            <TextField
              fullWidth
              sx={{ mb: 2 }}
              label="Proposal Title"
              required
              value={formData.proposal_title || ''}
              onChange={(e) => setFormData({ ...formData, proposal_title: e.target.value })}
            />
            <TextField
              fullWidth
              sx={{ mb: 2 }}
              type="number"
              label="Proposal Amount ($)"
              required
              value={formData.proposal_amount || opportunity?.estimated_value || ''}
              onChange={(e) => setFormData({ ...formData, proposal_amount: e.target.value })}
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Terms & Conditions"
              value={formData.proposal_terms || ''}
              onChange={(e) => setFormData({ ...formData, proposal_terms: e.target.value })}
            />
          </>
        );
      case 'contract_won':
        return (
          <>
            <TextField
              fullWidth
              sx={{ mb: 2 }}
              type="number"
              label="Contract Value ($)"
              required
              value={formData.contract_value || opportunity?.estimated_value || ''}
              onChange={(e) => setFormData({ ...formData, contract_value: e.target.value })}
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Contract Terms"
              value={formData.contract_terms || ''}
              onChange={(e) => setFormData({ ...formData, contract_terms: e.target.value })}
            />
          </>
        );
      case 'contract_lost':
        return (
          <>
            <TextField
              fullWidth
              sx={{ mb: 2 }}
              label="Loss Reason"
              required
              value={formData.loss_reason || ''}
              onChange={(e) => setFormData({ ...formData, loss_reason: e.target.value })}
            />
            <TextField
              fullWidth
              sx={{ mb: 2 }}
              label="Competitor (if known)"
              value={formData.competitor_won || ''}
              onChange={(e) => setFormData({ ...formData, competitor_won: e.target.value })}
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Additional Notes"
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
          </>
        );
      default:
        return null;
    }
  };
  
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Skeleton variant="rectangular" height={400} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={400} />
          </Grid>
        </Grid>
      </Container>
    );
  }
  
  if (error || !opportunity) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Opportunity not found'}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/stratagem')} sx={{ mt: 2 }}>
          Back to Stratagem Nexus
        </Button>
      </Container>
    );
  }
  
  const nextAction = getNextAction();
  const isTerminalState = opportunity.state === 'contract_won' || opportunity.state === 'contract_lost';
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/stratagem')} sx={{ mr: 2 }}>
          <BackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight={700}>
            {getSourceLabel()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {opportunity.source_engine} • {opportunity.vertical_slug || opportunity.geo_region || 'General'}
          </Typography>
        </Box>
        <Chip
          label={stateLabels[opportunity.state]}
          sx={{
            backgroundColor: stateColors[opportunity.state],
            color: 'white',
            fontWeight: 700,
            fontSize: '1rem',
            px: 2,
            py: 2.5,
          }}
        />
      </Box>
      
      {/* State Progress Stepper */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper
          alternativeLabel
          activeStep={getCurrentStepIndex()}
          connector={<ColorlibConnector />}
        >
          {stateOrder.map((state, index) => (
            <Step key={state} completed={index < getCurrentStepIndex()}>
              <StepLabel
                StepIconComponent={(props) => (
                  <ColorlibStepIcon
                    {...props}
                    completed={index < getCurrentStepIndex()}
                    active={index === getCurrentStepIndex()}
                    lost={opportunity.state === 'contract_lost'}
                    icon={stateIcons[state]}
                  />
                )}
              >
                {stateLabels[state]}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {opportunity.state === 'contract_lost' && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <strong>Lost:</strong> {opportunity.loss_reason || 'No reason specified'}
          </Alert>
        )}
      </Paper>
      
      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          {/* Primary Action Card */}
          {!isTerminalState && nextAction && (
            <Card
              sx={{
                mb: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                border: `2px solid ${theme.palette.primary.main}`,
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Next Step
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Move this opportunity forward in the pipeline
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handlePrimaryAction}
                    startIcon={stateIcons[nextAction.nextState]}
                    sx={{ flex: 1 }}
                  >
                    {nextAction.action}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleLoseAction}
                    startIcon={<LoseIcon />}
                  >
                    Mark Lost
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}
          
          {/* Won Card */}
          {opportunity.state === 'contract_won' && (
            <Card
              sx={{
                mb: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.15)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
                border: `2px solid ${theme.palette.success.main}`,
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <WinIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                <Typography variant="h4" fontWeight={700} color="success.main">
                  Contract Won!
                </Typography>
                <Typography variant="h3" fontWeight={700} sx={{ mt: 1 }}>
                  {formatCurrency(opportunity.contract_value)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Closed on {formatDateTime(opportunity.closed_at)}
                </Typography>
              </CardContent>
            </Card>
          )}
          
          {/* Timeline */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <HistoryIcon color="primary" />
                <Typography variant="h6">Activity Timeline</Typography>
              </Box>
              
              {history.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No actions recorded yet
                </Typography>
              ) : (
                <Box sx={{ pl: 2 }}>
                  {history.map((action, idx) => (
                    <Box
                      key={action.id}
                      sx={{
                        display: 'flex',
                        gap: 2,
                        pb: 2,
                        borderLeft: idx < history.length - 1 ? `2px solid ${theme.palette.divider}` : 'none',
                        pl: 2,
                        ml: 1,
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: -6,
                          top: 0,
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: stateColors[action.to_state] || theme.palette.primary.main,
                        },
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {action.action_type.replace(/_/g, ' ').toUpperCase()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {action.from_state && `${stateLabels[action.from_state]} → `}
                          {stateLabels[action.to_state]}
                        </Typography>
                        {action.action_data && Object.keys(action.action_data).length > 0 && (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                            {JSON.stringify(action.action_data).slice(0, 100)}...
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {formatDateTime(action.created_at)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Value Card */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <MoneyIcon color="warning" />
                <Typography variant="subtitle2" color="text.secondary">Opportunity Value</Typography>
              </Box>
              <Typography variant="h3" fontWeight={700}>
                {formatCurrency(opportunity.contract_value || opportunity.estimated_value)}
              </Typography>
              {opportunity.contract_value && opportunity.estimated_value && (
                <Typography variant="caption" color="text.secondary">
                  Originally estimated: {formatCurrency(opportunity.estimated_value)}
                </Typography>
              )}
            </CardContent>
          </Card>
          
          {/* Contact Info */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Contact Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {opportunity.contact_company && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <BusinessIcon fontSize="small" color="action" />
                  <Typography variant="body2">{opportunity.contact_company}</Typography>
                </Box>
              )}
              {opportunity.contact_name && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <PersonIcon fontSize="small" color="action" />
                  <Typography variant="body2">{opportunity.contact_name}</Typography>
                </Box>
              )}
              {opportunity.contact_email && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <EmailIcon fontSize="small" color="action" />
                  <Typography variant="body2">{opportunity.contact_email}</Typography>
                </Box>
              )}
              {opportunity.contact_phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <PhoneIcon fontSize="small" color="action" />
                  <Typography variant="body2">{opportunity.contact_phone}</Typography>
                </Box>
              )}
              
              {!opportunity.contact_name && !opportunity.contact_email && !opportunity.contact_company && (
                <Typography variant="body2" color="text.secondary">
                  No contact info recorded yet
                </Typography>
              )}
            </CardContent>
          </Card>
          
          {/* Timeline Dates */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Timeline
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {[
                { label: 'Created', date: opportunity.created_at },
                { label: 'Pursued', date: opportunity.pursued_at },
                { label: 'Outreach Sent', date: opportunity.outreach_sent_at },
                { label: 'Engaged', date: opportunity.engaged_at },
                { label: 'Proposal Sent', date: opportunity.proposal_sent_at },
                { label: 'Won', date: opportunity.won_at },
                { label: 'Lost', date: opportunity.lost_at },
              ].filter(item => item.date).map((item, idx) => (
                <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                  <Typography variant="body2" fontWeight={500}>{formatDateTime(item.date)}</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
          
          {/* Source Attribution */}
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Source Attribution
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Engine</Typography>
                <Chip label={opportunity.source_engine} size="small" />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Vertical</Typography>
                <Chip label={opportunity.vertical_slug || opportunity.geo_region || 'General'} size="small" variant="outlined" />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Confidence</Typography>
                <Chip
                  label={`${(parseFloat(String(opportunity.confidence_score)) * 100).toFixed(0)}%`}
                  size="small"
                  color={
                    parseFloat(String(opportunity.confidence_score)) >= 0.8 ? 'success' :
                    parseFloat(String(opportunity.confidence_score)) >= 0.5 ? 'warning' : 'default'
                  }
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Action Dialog */}
      <Dialog
        open={actionDialog.open}
        onClose={() => setActionDialog({ open: false, actionType: '', nextState: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{actionDialog.actionType}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {renderDialogContent()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setActionDialog({ open: false, actionType: '', nextState: null })}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={executeAction}
            disabled={actionLoading}
            color={actionDialog.nextState === 'contract_lost' ? 'error' : 'primary'}
          >
            {actionLoading ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OpportunityDetailPage;
